// src/utils/TexturePreloader.js
import * as THREE from "three";
import { withCrossOrigin } from "./ensureTextureCrossOrigin";
import { WOOD } from "../Helpers/WOOD";
import { WOODF } from "../Helpers/WOODF";
import { PAPER } from "../Helpers/PAPER";
import { METAL } from "../Helpers/METAL";
import { METALF } from "../Helpers/METALF";
import { COLORS } from "../Helpers/COLORS";
import { OTHERS } from "../Helpers/OTHERS";
import { useLoadingStore, LOADING_STAGES } from "./LoadingOrchestrator";

class TexturePreloader {
  constructor() {
    this.textureLoader = withCrossOrigin(new THREE.TextureLoader());
    this.loadedTextures = new Map();
    this.isPreloading = false;
  }

  async preloadAllTextures() {
    if (this.isPreloading) return;
    this.isPreloading = true;

    const allMaterials = [
      ...WOOD,
      ...WOODF,
      ...PAPER,
      ...METAL,
      ...METALF,
      ...COLORS,
      ...OTHERS,
    ];

    const texturePromises = [];

    for (const material of allMaterials) {
      const listName = material.listName || "UNKNOWN";
      // Load all possible texture types for each material
      const textureTypes = [
        { key: "texture", url: material.texture },
        { key: "normalMap", url: material.normalMap },
        { key: "roughnessMap", url: material.roughnessMap },
        { key: "metalnessMap", url: material.metalnessMap },
        { key: "aoMap", url: material.aoMap },
      ];

      for (const { key, url } of textureTypes) {
        if (url) {
          const promise = this.loadTexture(url).then((texture) => {
            // Configure texture based on material settings
            texture.repeat.set(material.size[0], material.size[1]);
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.offset.set(material.offset[0], material.offset[1]);
            texture.rotation = (Math.PI / 2) * material.rotation;
            if (key === "texture") {
              texture.colorSpace = material.space;
            }

            // Store the configured texture
            const textureKey = `${listName}_${material.name}_${key}`;
            this.loadedTextures.set(textureKey, texture);
          });
          texturePromises.push(promise);
        }
      }
    }

    try {
      await Promise.all(texturePromises);
    } catch (error) {
      console.error("Error preloading textures:", error);
    } finally {
      this.isPreloading = false;
    }
  }

  loadTexture(url) {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => resolve(texture),
        undefined,
        (error) => reject(error)
      );
    });
  }

  getTexture(listName, materialName, textureType) {
    const key = `${listName}_${materialName}_${textureType}`;
    return this.loadedTextures.get(key);
  }

  hasTexture(listName, materialName, textureType) {
    const key = `${listName}_${materialName}_${textureType}`;
    return this.loadedTextures.has(key);
  }
}

// Create a singleton instance
export const texturePreloader = new TexturePreloader();
